import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "react-select";
import "./InteractionContextMenu.css";
import api from "../../api";
import { DateTime } from "luxon";

const InteractionContextMenu = ({
  x,
  y,
  onSubmit,
  onClose,
  rowData,
  statusOptions = [],
  outcomeOptions = ["Successful", "Unsuccessful", "Pending"],
  nextStepOptions = ["No Action", "Follow-up Call", "Send Email", "Schedule Meeting"],
  showToast
}) => {
  const isNew = rowData.status === "New";

  const newRowInteractionOptions = [
    "First Call - No Answer",
    "First Call - Left VM",
    "First Call - Contacted",
    "First Email",
    "First Email - No Reply",
    "First Email - Replied",
    "First Meeting - Scheduled",
    "First Meeting - Rescheduled",
    "First Meeting - Completed",
    "Follow-up",
    "Status Update",
    "Other"
  ];

  const standardMainOptions = [
    { label: "Call", outcomeNeeded: false, nextStepNeeded: true, defaultFollowUp: 1, statusUpdateNeeded: false },
    { label: "Call - No Answer", outcomeNeeded: false, nextStepNeeded: true, defaultFollowUp: 1, statusUpdateNeeded: true },
    { label: "Call - Left VM", outcomeNeeded: false, nextStepNeeded: true, defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted" },
    { label: "Call - Contacted", outcomeNeeded: true, nextStepNeeded: true, defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted" },
    { label: "Email", outcomeNeeded: false, nextStepNeeded: true, defaultFollowUp: 1 },
    { label: "Email - No Reply", outcomeNeeded: false, nextStepNeeded: true, defaultFollowUp: 1 },
    { label: "Email - Replied", outcomeNeeded: true, nextStepNeeded: true, defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted" },
    { label: "Meeting", outcomeNeeded: false, nextStepNeeded: true, defaultFollowUp: 1 },
    { label: "Meeting - Rescheduled", outcomeNeeded: false, nextStepNeeded: true, defaultFollowUp: 1 },
    { label: "Meeting - Completed", outcomeNeeded: true, nextStepNeeded: true, defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted" },
    { label: "Follow-up" },
    { label: "Status Update" },
    { label: "Other" }
  ];

  const newRowConfigMapping = {
    "First Call - No Answer": { defaultFollowUp: 1, statusUpdateNeeded: false, outcomeNeeded: false },
    "First Call - Left VM": { defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted", outcomeNeeded: false },
    "First Call - Contacted": { defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted", outcomeNeeded: true },
    "First Email - No Reply": { defaultFollowUp: 1, statusUpdateNeeded: false, outcomeNeeded: false },
    "First Email - Replied": { defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted", outcomeNeeded: true },
    "First Meeting - Rescheduled": { defaultFollowUp: 1, statusUpdateNeeded: false, outcomeNeeded: false },
    "First Meeting - Completed": { defaultFollowUp: 1, statusUpdateNeeded: true, autoStatus: "Contacted", outcomeNeeded: true }
  };

  const newRowOptions = newRowInteractionOptions.map(opt => ({
    value: opt,
    label: opt
  }));
  const standardOptions = standardMainOptions.map(opt => ({
    value: opt.label,
    label: opt.label,
    ...opt
  }));

  const [selectedOptions, setSelectedOptions] = useState(
    isNew ? [newRowInteractionOptions[0]] : [standardMainOptions[0].label]
  );
  const [note, setNote] = useState("");
  const [newStatus, setNewStatus] = useState(rowData?.status || (statusOptions[0] || "New"));
  const [outcome, setOutcome] = useState(outcomeOptions[0]);
  const [nextStep, setNextStep] = useState(nextStepOptions[0]);
  const [followUpDate, setFollowUpDate] = useState("");

  const [position, setPosition] = useState({ x, y });
  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const menuRef = useRef(null);

  const handleMouseDown = (e) => {
    draggingRef.current = true;
    offsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (draggingRef.current) {
        setPosition({
          x: e.clientX - offsetRef.current.x,
          y: e.clientY - offsetRef.current.y,
        });
      }
    },
    []
  );

  const handleMouseUp = () => {
    draggingRef.current = false;
  };

  useEffect(() => {
    const handleMove = (e) => handleMouseMove(e);
    const handleUp = () => handleMouseUp();
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [handleMouseMove]);

  useEffect(() => {
    const currentOption = selectedOptions[0];
    const computeFollowUpDate = (dayOffset) => {
      let followUp = DateTime.local().plus({ days: dayOffset });
      if (followUp.hour < 8) {
        followUp = followUp.set({ hour: 8, minute: 0 });
      } else if (followUp.hour >= 17) {
        followUp = followUp.set({ hour: 17, minute: 0 });
      }
      return followUp.toFormat("yyyy-MM-dd'T'HH:mm");
    };

    if (isNew) {
      const config = newRowConfigMapping[currentOption];
      if (config && config.defaultFollowUp) {
        setFollowUpDate(computeFollowUpDate(config.defaultFollowUp));
      } else {
        setFollowUpDate("");
      }
      if (config && config.statusUpdateNeeded && config.autoStatus) {
        setNewStatus(config.autoStatus);
      }
    } else {
      const config = standardMainOptions.find(opt => opt.label === currentOption);
      if (config && config.defaultFollowUp) {
        setFollowUpDate(computeFollowUpDate(config.defaultFollowUp));
      } else {
        setFollowUpDate("");
      }
      if (config && config.statusUpdateNeeded && config.autoStatus) {
        setNewStatus(config.autoStatus);
      }
    }
  }, [selectedOptions, isNew]);

  let outcomeNeeded = false;
  if (isNew) {
    outcomeNeeded = selectedOptions.some((option) => {
      const config = newRowConfigMapping[option];
      return config ? config.outcomeNeeded !== false : true;
    });
  } else {
    outcomeNeeded = selectedOptions.some((option) => {
      const config = standardMainOptions.find(opt => opt.label === option);
      return config ? config.outcomeNeeded !== false : true;
    });
  }

  const showStatusUpdateDropdown = selectedOptions.includes("Status Update");

  const handleSubmit = async () => {
    if (!selectedOptions.length) {
      showToast("Please select an interaction type", "error");
      return;
    }

    // Get the current user ID from sessionStorage
    const currentUserStr = sessionStorage.getItem("user");
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
    const currentUserId = currentUser?.id;

    const interactions = selectedOptions.join(", ");
    let payload = {
      lead_id: rowData.id,
      note,
      next_step: nextStep,
      follow_up_date: followUpDate,
      interaction: interactions,
      new_status: null,
      old_status: null,
      created_at: DateTime.local().toISO(),
      added_by: currentUserId
    };
  
    if (selectedOptions.includes("Status Update")) {
      payload.new_status = newStatus;
      payload.old_status = rowData.status;
    } else {
      if (isNew) {
        const config = newRowConfigMapping[selectedOptions[0]];
        if (config && config.statusUpdateNeeded) {
          payload.new_status = newStatus;
          payload.old_status = rowData.status;
        }
      } else {
        const config = standardMainOptions.find(opt => opt.label === selectedOptions[0]);
        if (config && config.statusUpdateNeeded) {
          payload.new_status = newStatus;
          payload.old_status = rowData.status;
        }
      }
    }
    if (outcomeNeeded) {
      payload.outcome = outcome;
    }
  
    console.log("InteractionContextMenu: Submit button clicked.");
    console.log("InteractionContextMenu: Payload to be sent:", payload);
  
    try {
      showToast("Interaction(s) submitted successfully!", "success");
  
      await onSubmit(payload);
  
      if (isNew) {
        setSelectedOptions([newRowInteractionOptions[0]]);
      } else {
        setSelectedOptions([standardMainOptions[0].label]);
      }
      setOutcome(outcomeOptions[0]);
      setNextStep(nextStepOptions[0]);
      setFollowUpDate("");
      setNewStatus(rowData?.status || (statusOptions[0] || "New"));
      setNote("");
  
      onClose();
    } catch (error) {
      console.error("InteractionContextMenu: Error submitting interaction:", error);
      let errorMsg = "Error submitting interaction";
      if (error.response && error.response.data && error.response.data.error) {
        errorMsg = error.response.data.error;
      }
      showToast(errorMsg, "error");
    }
  };
  

  return (
    <div
      ref={menuRef}
      className="interaction-context-menu"
      style={{ position: "absolute", top: position.y, left: position.x, zIndex: 9999 }}
      onClick={(e) => e.stopPropagation()}
    >
      <button className="close-button" onClick={onClose}>×</button>

      <div
        className="drag-handle"
        onMouseDown={handleMouseDown}
      >
        {rowData.name} - {rowData.company}
      </div>
      {isNew ? (
        <div className="interaction-dropdown">
          <label htmlFor="interaction-select">Interaction(s):</label>
          <Select
            id="interaction-select"
            options={newRowOptions}
            isMulti
            value={newRowOptions.filter(option => selectedOptions.includes(option.value))}
            onChange={(selected) => {
              const values = selected ? selected.map(option => option.value) : [];
              setSelectedOptions(values);
            }}
          />
        </div>
      ) : (
        <div className="interaction-dropdown">
          <label htmlFor="interaction-select">Interaction(s):</label>
          <Select
            id="interaction-select"
            options={standardOptions}
            isMulti
            value={standardOptions.filter(option => selectedOptions.includes(option.value))}
            onChange={(selected) => {
              const values = selected ? selected.map(option => option.value) : [];
              setSelectedOptions(values);
            }}
          />
        </div>
      )}

      {showStatusUpdateDropdown && (
        <div className="interaction-new-status">
          <label htmlFor="new-status-select">New Status:</label>
          <Select
            id="new-status-select"
            options={statusOptions.map(status => ({ value: status, label: status }))}
            value={{ value: newStatus, label: newStatus }}
            onChange={(selected) => setNewStatus(selected.value)}
          />
        </div>
      )}

      {outcomeNeeded && (
        <div className="interaction-outcome">
          <label htmlFor="outcome-select">Outcome:</label>
          <Select
            id="outcome-select"
            options={outcomeOptions.map(option => ({ value: option, label: option }))}
            value={{ value: outcome, label: outcome }}
            onChange={(selected) => setOutcome(selected.value)}
          />
        </div>
      )}

      <div className="interaction-next-step">
        <label htmlFor="next-step-select">Next Step:</label>
        <Select
          id="next-step-select"
          options={nextStepOptions.map(option => ({ value: option, label: option }))}
          value={{ value: nextStep, label: nextStep }}
          onChange={(selected) => setNextStep(selected.value)}
        />
      </div>

      <div className="interaction-follow-up-date">
        <label htmlFor="follow-up-date">Follow Up Date:</label>
        <input
          id="follow-up-date"
          type="datetime-local"
          step="900"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
        />
      </div>

      <div className="interaction-note">
        <label htmlFor="interaction-note">Note:</label>
        <textarea
          id="interaction-note"
          placeholder="Type your note here..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <button onClick={handleSubmit}>Submit</button>
    </div>
  );
};

export default InteractionContextMenu;
